import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Snackbar,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import AddIcon from "@mui/icons-material/Add";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ClearIcon from "@mui/icons-material/Clear";
import CAMHeader from "../../components/Header/CAMHeader";
import CAMDrawer from "../../components/LeftSideBar/CAMDrawer";
import InvestigatorContainer from "../../components/contsiners/InvestigatorContainer";
import BreadcrumbHeader from "../../components/Header/BreadCrumHeader";
import PaginationComponent from "../../components/Pagination";
import Loader from "../../components/Loader/loader";
import PermissionSummary from "../../components/PermissionSummary/PermissionSummary";
import UserManagementService from "../../services/userManagementService";
import {
  USER_ACCESS_STATUS_OPTIONS,
  USER_MANAGEMENT_TEXT,
  USER_MANAGEMENT_PAGE_SIZE,
  downloadAuditCsv,
  downloadFileFromUrl,
  getApiErrorMessage,
  getPaginatedSerialNumber,
  getPaginationCount,
  getRbacExportTaskId,
  normalizeAuditActivityFilters,
  normalizeAuditTrail,
  normalizeGroups,
  normalizePermissionGroups,
  normalizeUserAccessLevelFilters,
  normalizeUserRoleFilters,
  normalizeUsers,
} from "../../utils/userManagementUtility";
import { hasAnyPermission, hasPermission } from "../../utils/permissions";
import {
  PERMISSION_MODULES,
  USER_ACCESS_MANAGEMENT_PERMISSIONS,
} from "../../constants/permissions";

import "./userManagement.scss";

const Layout = ({ children }) => (
  <>
    <CAMHeader />
    <CAMDrawer />
    <InvestigatorContainer>
      <main className="user-management-page">{children}</main>
    </InvestigatorContainer>
  </>
);
const PrimaryButton = (props) => (
  <Button variant="contained" disableElevation {...props} />
);
const PageTitle = ({ children }) => (
  <Box px={1} py={0.875} borderBottom="1px solid #d9dde2">
    <Typography variant="subtitle1" fontWeight={500}>
      {children}
    </Typography>
  </Box>
);

const ExpandableSummary = ({ items = [], visibleCount = 3 }) => {
  const [expanded, setExpanded] = useState(false);
  const summary = items.join(", ");
  const isExpandable = items.length > visibleCount || summary.length > 90;
  const visibleSummary = (expanded ? items : items.slice(0, visibleCount)).join(
    ", ",
  );

  if (!items.length) return <Typography component="span">-</Typography>;

  return (
    <Box
      className={`group-summary ${
        expanded ? "group-summary--expanded" : ""
      } ${!isExpandable ? "group-summary--complete" : ""}`}
    >
      <Typography component="span" variant="body2">
        {visibleSummary}
      </Typography>
      {isExpandable && (
        <Button
          className="summary-more-button"
          size="small"
          aria-expanded={expanded}
          onClick={() => setExpanded((current) => !current)}
        >
          {expanded ? "Less" : "... More"}
        </Button>
      )}
    </Box>
  );
};

const Crumb = ({ current, second, onHome, children }) => (
  <Box className="um-breadcrumb-row">
    <BreadcrumbHeader
      first="User Access Management"
      second={second}
      third={current}
      onBackClick={onHome}
    />
    {children}
  </Box>
);

const ManagementTabs = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const value = location.pathname.includes("access-groups")
    ? "access-groups"
    : location.pathname.includes("audit-trail")
      ? "audit-trail"
      : "users";
  const canViewUsers = hasPermission(
    PERMISSION_MODULES.USER_ACCESS_MANAGEMENT,
    USER_ACCESS_MANAGEMENT_PERMISSIONS.VIEW_USER_LIST,
  );
  const canViewGroups = hasPermission(
    PERMISSION_MODULES.USER_ACCESS_MANAGEMENT,
    USER_ACCESS_MANAGEMENT_PERMISSIONS.VIEW_ACCESS_GROUP_LIST,
  );
  const canViewAudit = hasPermission(
    PERMISSION_MODULES.USER_ACCESS_MANAGEMENT,
    USER_ACCESS_MANAGEMENT_PERMISSIONS.VIEW_AUDIT_TRAIL,
  );

  return (
    <Box className="um-tabs-row">
      <Tabs
        value={value}
        onChange={(_, nextValue) =>
          navigate(
            nextValue === "users"
              ? "/user-management"
              : `/user-management/${nextValue}`,
          )
        }
        aria-label="User management sections"
      >
        {canViewUsers && <Tab label="Users" value="users" />}
        {canViewGroups && <Tab label="Access Groups" value="access-groups" />}
        {canViewAudit && <Tab label="Audit Trail" value="audit-trail" />}
      </Tabs>
      {children}
    </Box>
  );
};

const PermissionSelector = ({
  groups,
  selected,
  onChange,
  showSelectAll = false,
  disabled = false,
}) => {
  const all = groups.flatMap((group) => group.items.map((item) => item.id));
  const toggleItems = (items) => {
    const ids = items.map((item) => item.id);
    const checked = ids.every((id) => selected.includes(id));
    onChange(
      checked
        ? selected.filter((id) => !ids.includes(id))
        : [...new Set([...selected, ...ids])],
    );
  };
  return (
    <div className="permission-selector">
      {showSelectAll && (
        <div className="select-all-row">
          <FormControlLabel
            control={
              <Checkbox
                checked={selected.length === all.length}
                disabled={disabled}
                indeterminate={
                  selected.length > 0 && selected.length < all.length
                }
                onChange={() =>
                  onChange(selected.length === all.length ? [] : all)
                }
              />
            }
            label={
              <span>
                <strong>Select All Permissions</strong>
                <small>
                  Check or clear all actions across all categories below
                </small>
              </span>
            }
          />
        </div>
      )}
      {groups.map((group) => (
        <section className="permission-group" key={group.label}>
          <div className="permission-group__header">
            <FormControlLabel
              control={
                <Checkbox
                  checked={group.items.every((item) =>
                    selected.includes(item.id),
                  )}
                  disabled={disabled}
                  indeterminate={
                    group.items.some((item) => selected.includes(item.id)) &&
                    !group.items.every((item) => selected.includes(item.id))
                  }
                  onChange={() => toggleItems(group.items)}
                />
              }
              label={group.label}
            />
          </div>
          <div className="permission-group__items">
            {group.items.map((item) => (
              <FormControlLabel
                key={item.id}
                control={
                  <Checkbox
                    checked={selected.includes(item.id)}
                    disabled={disabled}
                    onChange={() =>
                      onChange(
                        selected.includes(item.id)
                          ? selected.filter((value) => value !== item.id)
                          : [...selected, item.id],
                      )
                    }
                  />
                }
                label={item.label}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};

export const UserList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const canEditUser = hasPermission(
    PERMISSION_MODULES.USER_ACCESS_MANAGEMENT,
    USER_ACCESS_MANAGEMENT_PERMISSIONS.EDIT_USER,
  );
  const canUpdateUserStatus = hasPermission(
    PERMISSION_MODULES.USER_ACCESS_MANAGEMENT,
    USER_ACCESS_MANAGEMENT_PERMISSIONS.UPDATE_USER_STATUS,
  );
  const canOpenUserEditor = canEditUser || canUpdateUserStatus;
  const canExportUsers = hasPermission(
    PERMISSION_MODULES.USER_ACCESS_MANAGEMENT,
    USER_ACCESS_MANAGEMENT_PERMISSIONS.EXPORT_USERS,
  );
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [toast, setToast] = useState(location.state?.toast || "");
  const [downloading, setDownloading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [accessLevels, setAccessLevels] = useState([]);
  const [selectedAccessLevelIds, setSelectedAccessLevelIds] = useState([]);
  const [selectedAccessStatus, setSelectedAccessStatus] = useState("");
  const hasActiveUserFilters =
    selectedRoleIds.length > 0 ||
    selectedAccessLevelIds.length > 0 ||
    Boolean(selectedAccessStatus);
  const rows = users;
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setPage(1);
      setDebouncedSearch(search.trim());
    }, 500);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    const params = {
      page,
      page_size: USER_MANAGEMENT_PAGE_SIZE,
    };
    if (debouncedSearch) params.search = debouncedSearch;
    if (selectedRoleIds.length) {
      params.group_id = selectedRoleIds.join(",");
    }
    if (selectedAccessLevelIds.length) {
      params.module_ids = selectedAccessLevelIds.join(",");
    }
    if (selectedAccessStatus) {
      params.status_type = selectedAccessStatus;
    }
    UserManagementService.getUsers(params)
      .then((response) => {
        if (!active) return;
        setUsers(normalizeUsers(response));
        const availableRoles = normalizeUserRoleFilters(response);
        const availableAccessLevels = normalizeUserAccessLevelFilters(response);
        if (availableAccessLevels.length) {
          setAccessLevels(availableAccessLevels);
        }
        if (availableRoles.length) {
          setRoles(availableRoles);
        }
        setPageCount(getPaginationCount(response, USER_MANAGEMENT_PAGE_SIZE));
      })
      .catch(
        (requestError) =>
          active &&
          setError(
            getApiErrorMessage(
              requestError,
              USER_MANAGEMENT_TEXT.LOAD_USERS_ERROR,
            ),
          ),
      )
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [
    debouncedSearch,
    page,
    selectedAccessLevelIds,
    selectedAccessStatus,
    selectedRoleIds,
  ]);
  const closeToast = () => {
    setToast("");
    navigate(location.pathname, { replace: true, state: null });
  };
  const handleResetFilters = () => {
    setPage(1);
    setSelectedRoleIds([]);
    setSelectedAccessLevelIds([]);
    setSelectedAccessStatus("");
  };
  const handleDownload = async () => {
    if (!canExportUsers) return;
    setDownloading(true);
    setError("");
    try {
      const response = await UserManagementService.exportUsers();
      const taskId = getRbacExportTaskId(response);
      if (!taskId) throw new Error("User export did not return a task ID.");
      const exportResult = await UserManagementService.waitForExport(taskId);
      downloadFileFromUrl(exportResult.downloadUrl, "users.xlsx");
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, "Unable to download users."));
    } finally {
      setDownloading(false);
    }
  };
  return (
    <Layout>
      <PageTitle>User Access Management</PageTitle>
      <ManagementTabs>
        <div className="um-toolbar__actions">
          <div className="um-search">
            <TextField
              size="small"
              placeholder="Search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              inputProps={{ "aria-label": "Search users" }}
            />
            <SearchOutlinedIcon />
          </div>
          {roles.length > 0 && (
            <Select
              className={`user-role-filter ${
                selectedRoleIds.length
                  ? "user-role-filter--active"
                  : ""
              }`}
              size="small"
              multiple
              value={selectedRoleIds}
              onChange={(event) => {
                const selectedValues =
                  typeof event.target.value === "string"
                    ? event.target.value.split(",")
                    : event.target.value;
                const nextIds = selectedValues.includes("all")
                  ? []
                  : selectedValues.map(Number).filter(Number.isFinite);
                setPage(1);
                setSelectedRoleIds(nextIds);
              }}
              displayEmpty
              renderValue={(selectedIds) => {
                if (!selectedIds.length) {
                  return USER_MANAGEMENT_TEXT.USER_ROLE_FILTER_ALL;
                }
                return roles
                  .filter((userRole) => selectedIds.includes(userRole.id))
                  .map((userRole) => userRole.name)
                  .join(", ");
              }}
              startAdornment={
                <InputAdornment position="start">
                  <FilterAltOutlinedIcon fontSize="small" />
                </InputAdornment>
              }
              MenuProps={{
                PaperProps: {
                  className: "user-access-level-filter-menu",
                },
              }}
              inputProps={{ "aria-label": "Filter users by role" }}
            >
              <MenuItem value="all">
                <Checkbox size="small" checked={!selectedRoleIds.length} />
                {USER_MANAGEMENT_TEXT.USER_ROLE_FILTER_ALL}
              </MenuItem>
              {roles.map((userRole) => (
                <MenuItem key={userRole.id} value={userRole.id}>
                  <Checkbox
                    size="small"
                    checked={selectedRoleIds.includes(userRole.id)}
                  />
                  {userRole.name}
                </MenuItem>
              ))}
            </Select>
          )}
          {accessLevels.length > 0 && (
            <Select
              className={`user-access-level-filter ${
                selectedAccessLevelIds.length
                  ? "user-access-level-filter--active"
                  : ""
              }`}
              size="small"
              multiple
              value={selectedAccessLevelIds}
              onChange={(event) => {
                const selectedValues =
                  typeof event.target.value === "string"
                    ? event.target.value.split(",")
                    : event.target.value;
                const nextIds = selectedValues.includes("all")
                  ? []
                  : selectedValues.map(Number).filter(Number.isFinite);
                setPage(1);
                setSelectedAccessLevelIds(nextIds);
              }}
              displayEmpty
              renderValue={(selectedIds) => {
                if (!selectedIds.length) {
                  return USER_MANAGEMENT_TEXT.ACCESS_LEVEL_FILTER_LABEL;
                }
                const selectedNames = accessLevels
                  .filter((accessLevel) => selectedIds.includes(accessLevel.id))
                  .map((accessLevel) => accessLevel.name);
                return selectedNames.join(", ");
              }}
              startAdornment={
                <InputAdornment position="start">
                  <FilterAltOutlinedIcon fontSize="small" />
                </InputAdornment>
              }
              MenuProps={{
                PaperProps: {
                  className: "user-access-level-filter-menu",
                },
              }}
              inputProps={{ "aria-label": "Filter users by access level" }}
            >
              <MenuItem value="all">
                <Checkbox
                  size="small"
                  checked={selectedAccessLevelIds.length === 0}
                />
                {USER_MANAGEMENT_TEXT.ACCESS_LEVEL_FILTER_ALL}
              </MenuItem>
              {accessLevels.map((accessLevel) => (
                <MenuItem key={accessLevel.id} value={accessLevel.id}>
                  <Checkbox
                    size="small"
                    checked={selectedAccessLevelIds.includes(accessLevel.id)}
                  />
                  {accessLevel.name}
                </MenuItem>
              ))}
            </Select>
          )}
          <Select
            className={`user-access-status-filter ${
              selectedAccessStatus ? "user-access-status-filter--active" : ""
            }`}
            size="small"
            value={selectedAccessStatus}
            onChange={(event) => {
              setPage(1);
              setSelectedAccessStatus(event.target.value);
            }}
            displayEmpty
            startAdornment={
              <InputAdornment position="start">
                <FilterAltOutlinedIcon fontSize="small" />
              </InputAdornment>
            }
            MenuProps={{
              PaperProps: {
                className: "user-access-level-filter-menu",
              },
            }}
            inputProps={{ "aria-label": "Filter users by access status" }}
          >
            <MenuItem value="">
              {USER_MANAGEMENT_TEXT.ACCESS_STATUS_FILTER_ALL}
            </MenuItem>
            {USER_ACCESS_STATUS_OPTIONS.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </Select>
          <Button
            className="user-filter-reset-button"
            variant="outlined"
            startIcon={<ClearIcon fontSize="small" />}
            onClick={handleResetFilters}
            disabled={!hasActiveUserFilters}
          >
            {USER_MANAGEMENT_TEXT.RESET_FILTERS}
          </Button>
          {canExportUsers && (
            <PrimaryButton
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={handleDownload}
              disabled={downloading}
            >
              {downloading ? "Downloading..." : "Download"}
            </PrimaryButton>
          )}
        </div>
      </ManagementTabs>
      <div className="um-table-wrap">
        {error && <Alert severity="error">{error}</Alert>}
        <table className="um-table user-table">
          <thead>
            <tr>
              <th className="um-serial-column">Sr No.</th>
              <th>User Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Access Level</th>
              <th>Status</th>
              <th>Access Status</th>
              <th>Last Login</th>
              {canOpenUserEditor && <th>Action</th>}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr className="um-loading-row">
                <td
                  className="um-loading-cell"
                  colSpan={canOpenUserEditor ? 9 : 8}
                >
                  <Loader size={36} />
                </td>
              </tr>
            )}
            {!loading && !error && rows.length === 0 && (
              <tr>
                <td colSpan={canOpenUserEditor ? 9 : 8}>
                  <Box py={6} textAlign="center">
                    <Typography color="text.secondary">
                      {USER_MANAGEMENT_TEXT.NO_USERS}
                    </Typography>
                  </Box>
                </td>
              </tr>
            )}
            {!loading &&
              rows.map((user, index) => (
                <tr key={user.id}>
                  <td className="um-serial-column">
                    {getPaginatedSerialNumber(
                      page,
                      USER_MANAGEMENT_PAGE_SIZE,
                      index,
                    )}
                  </td>
                  <td>{user.name}</td>
                  <td>{user.email}</td>
                  <td>
                    <ExpandableSummary items={user.roles} visibleCount={1} />
                  </td>
                  <td>
                    <ExpandableSummary items={user.access} expandLongContent />
                  </td>
                  <td>
                    <span
                      className={`status status--${user.status.toLowerCase()}`}
                    >
                      {user.status}
                    </span>
                  </td>
                  <td>{user.accessStatus}</td>
                  <td>{user.lastLogin}</td>
                  {canOpenUserEditor && (
                    <td>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() =>
                          navigate(`/user-management/users/${user.id}/edit`, {
                            state: { user },
                          })
                        }
                      >
                        Edit
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <PaginationComponent
        count={pageCount}
        page={page}
        onChange={(_, value) => setPage(value)}
      />
      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={closeToast}
      >
        <Alert severity="success" variant="filled" onClose={closeToast}>
          {toast}
        </Alert>
      </Snackbar>
    </Layout>
  );
};

export const EditUser = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams();
  const canEditUser = hasPermission(
    PERMISSION_MODULES.USER_ACCESS_MANAGEMENT,
    USER_ACCESS_MANAGEMENT_PERMISSIONS.EDIT_USER,
  );
  const canUpdateStatus = hasPermission(
    PERMISSION_MODULES.USER_ACCESS_MANAGEMENT,
    USER_ACCESS_MANAGEMENT_PERMISSIONS.UPDATE_USER_STATUS,
  );
  const [user, setUser] = useState(null);
  const [groups, setGroups] = useState([]);
  const [permissionGroups, setPermissionGroups] = useState([]);
  const [groupIds, setGroupIds] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    let mounted = true;
    const selectedUser = location.state?.user;
    const loadedUser =
      String(selectedUser?.id) === String(userId) ? selectedUser : null;

    Promise.all([
      UserManagementService.getGroups(),
      UserManagementService.getModulesAndFeatures(),
    ])
      .then(([groupsResponse, permissionsResponse]) => {
        if (!mounted) return;
        const loadedGroups = normalizeGroups(groupsResponse);
        setUser(loadedUser || null);
        setGroups(loadedGroups);
        setPermissionGroups(normalizePermissionGroups(permissionsResponse));
        setGroupIds(loadedUser?.groupIds || []);
        setIsActive(loadedUser?.isActive ?? true);
        setPermissions(
          loadedGroups
            .filter((group) => loadedUser?.groupIds.includes(Number(group.id)))
            .flatMap((group) => group.featureIds),
        );
      })
      .catch((error) =>
        setMessage({
          severity: "error",
          text: getApiErrorMessage(
            error,
            USER_MANAGEMENT_TEXT.LOAD_USERS_ERROR,
          ),
        }),
      )
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [location.state, userId]);

  const handleGroupsChange = (event) => {
    const nextIds = event.target.value.map(Number);
    setGroupIds(nextIds);
    setPermissions(
      groups
        .filter((group) => nextIds.includes(Number(group.id)))
        .flatMap((group) => group.featureIds),
    );
  };

  const handleSave = async () => {
    if (!canEditUser && !canUpdateStatus) return;
    setSaving(true);
    setMessage(null);
    try {
      const payload = {};
      if (canEditUser) payload.groupIds = groupIds;
      if (canUpdateStatus) payload.isActive = isActive;
      await UserManagementService.updateUser(userId, payload);
      navigate("/user-management", {
        replace: true,
        state: { toast: USER_MANAGEMENT_TEXT.UPDATE_USER_SUCCESS },
      });
    } catch (error) {
      setMessage({
        severity: "error",
        text: getApiErrorMessage(error, USER_MANAGEMENT_TEXT.UPDATE_USER_ERROR),
      });
    } finally {
      setSaving(false);
    }
  };
  return (
    <Layout>
      <PageTitle>Edit User Role</PageTitle>
      <Crumb
        current="Edit User Role"
        onHome={() => navigate("/user-management")}
      />
      {message && <Alert severity={message.severity}>{message.text}</Alert>}
      <div className="edit-user-card">
        {loading && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <CircularProgress size={36} aria-label="Loading user" />
          </Box>
        )}
        {!loading && !user && <Alert severity="warning">User not found.</Alert>}
        {!loading && user && (
          <div className="edit-user-grid">
            <div className="user-fields">
              <label>
                User Name
                <TextField value={user.name} disabled fullWidth />
              </label>
              <label>
                Email Address
                <TextField value={user.email} disabled fullWidth />
              </label>
              <label>
                Role Selector
                <Select
                  multiple
                  value={groupIds}
                  onChange={handleGroupsChange}
                  fullWidth
                  disabled={!canEditUser}
                  sx={{
                    backgroundColor: groupIds.length
                      ? "rgba(25, 90, 156, 0.08)"
                      : "background.paper",
                    "& .MuiSelect-select": {
                      fontWeight: groupIds.length ? 600 : 400,
                    },
                  }}
                >
                  {groups.map((option) => (
                    <MenuItem
                      key={option.id}
                      value={Number(option.id)}
                      sx={{
                        "&.Mui-selected": {
                          backgroundColor: "rgba(25, 90, 156, 0.18)",
                          fontWeight: 600,
                        },
                        "&.Mui-selected:hover": {
                          backgroundColor: "rgba(25, 90, 156, 0.24)",
                        },
                      }}
                    >
                      {option.name}
                    </MenuItem>
                  ))}
                </Select>
              </label>
              <div className="status-control">
                <span>User Status</span>
                <div>
                  <Switch
                    checked={isActive}
                    onChange={(event) => setIsActive(event.target.checked)}
                    color="success"
                    disabled={!canUpdateStatus}
                  />
                  <span
                    className={isActive ? "active-account" : "inactive-account"}
                  >
                    {isActive ? "Active Account" : "Inactive Account"}
                  </span>
                </div>
              </div>
            </div>
            <div>
              <span className="field-caption">Access Level Permissions</span>
              <small className="role-caption">{user.role}</small>
              <PermissionSelector
                groups={permissionGroups}
                selected={permissions}
                onChange={setPermissions}
                disabled
              />
            </div>
          </div>
        )}
      </div>
      <div className="sticky-actions">
        <Button variant="outlined" onClick={() => navigate("/user-management")}>
          Cancel
        </Button>
        <PrimaryButton
          onClick={handleSave}
          disabled={saving || loading || !user}
        >
          {saving ? "Saving..." : "Save Changes"}
        </PrimaryButton>
      </div>
    </Layout>
  );
};

export const AccessGroupList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const canCreateGroup = hasPermission(
    PERMISSION_MODULES.USER_ACCESS_MANAGEMENT,
    USER_ACCESS_MANAGEMENT_PERMISSIONS.CREATE_ACCESS_GROUP,
  );
  const canEditGroup = hasAnyPermission(
    PERMISSION_MODULES.USER_ACCESS_MANAGEMENT,
    [
      USER_ACCESS_MANAGEMENT_PERMISSIONS.EDIT_ACCESS_GROUP,
      USER_ACCESS_MANAGEMENT_PERMISSIONS.UPDATE_ACCESS_GROUP_STATUS,
      USER_ACCESS_MANAGEMENT_PERMISSIONS.UPDATE_ACCESS_GROUP_PERMISSIONS,
    ],
  );
  const canExportGroups = hasPermission(
    PERMISSION_MODULES.USER_ACCESS_MANAGEMENT,
    USER_ACCESS_MANAGEMENT_PERMISSIONS.EXPORT_ACCESS_GROUPS,
  );
  const [search, setSearch] = useState("");
  const [groups, setGroups] = useState([]);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState(location.state?.toast || "");
  useEffect(() => {
    let mounted = true;
    UserManagementService.getGroups({
      page,
      page_size: USER_MANAGEMENT_PAGE_SIZE,
    })
      .then((response) => {
        if (!mounted) return;
        setGroups(normalizeGroups(response));
        setPageCount(getPaginationCount(response, USER_MANAGEMENT_PAGE_SIZE));
      })
      .catch(
        (requestError) =>
          mounted &&
          setError(
            getApiErrorMessage(
              requestError,
              USER_MANAGEMENT_TEXT.LOAD_GROUPS_ERROR,
            ),
          ),
      )
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [page]);
  const rows = groups.filter((group) =>
    `${group.name} ${group.description} ${group.summary}`
      .toLowerCase()
      .includes(search.toLowerCase()),
  );
  const handleDownload = async () => {
    if (!canExportGroups) return;
    setDownloading(true);
    setError("");
    try {
      const response = await UserManagementService.exportGroups();
      const taskId = getRbacExportTaskId(response);
      if (!taskId) {
        throw new Error("Access group export did not return a task ID.");
      }
      const exportResult = await UserManagementService.waitForExport(taskId);
      downloadFileFromUrl(exportResult.downloadUrl, "access-groups.xlsx");
    } catch (requestError) {
      setError(
        getApiErrorMessage(requestError, "Unable to download access groups."),
      );
    } finally {
      setDownloading(false);
    }
  };
  const closeToast = () => {
    setToast("");
    navigate(location.pathname, { replace: true, state: null });
  };
  return (
    <Layout>
      <PageTitle>Access Groups</PageTitle>
      <ManagementTabs>
        <div className="group-toolbar">
          <TextField
            size="small"
            placeholder="Search Access Groups..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          {canExportGroups && (
            <PrimaryButton
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={handleDownload}
              disabled={downloading}
            >
              {downloading ? "Downloading..." : "Download"}
            </PrimaryButton>
          )}
          {canCreateGroup && (
            <PrimaryButton
              startIcon={<AddIcon />}
              onClick={() => navigate("/user-management/access-groups/create")}
            >
              Create Access Group
            </PrimaryButton>
          )}
        </div>
      </ManagementTabs>
      <div className="um-table-wrap group-table-wrap">
        {error && <Alert severity="error">{error}</Alert>}
        <table className="um-table group-table">
          <thead>
            <tr>
              <th className="um-serial-column">Sr No.</th>
              <th>Group Name</th>
              <th>Description</th>
              <th>Role Permissions Summary</th>
              <th>Created Date</th>
              <th>Updated Date</th>
              {canEditGroup && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr className="um-loading-row">
                <td className="um-loading-cell" colSpan={canEditGroup ? 7 : 6}>
                  <Loader size={36} />
                </td>
              </tr>
            )}
            {!loading && !error && rows.length === 0 && (
              <tr>
                <td colSpan={canEditGroup ? 7 : 6}>
                  <Box py={6} textAlign="center">
                    <Typography color="text.secondary">
                      {USER_MANAGEMENT_TEXT.NO_GROUPS}
                    </Typography>
                  </Box>
                </td>
              </tr>
            )}
            {!loading &&
              rows.map((group, index) => (
                <tr key={group.id}>
                  <td className="um-serial-column">
                    {getPaginatedSerialNumber(
                      page,
                      USER_MANAGEMENT_PAGE_SIZE,
                      index,
                    )}
                  </td>
                  <td>
                    <strong>{group.name}</strong>
                    {group.isNew && <em>NEW</em>}
                  </td>
                  <td className="group-description">
                    {group.description || "-"}
                  </td>
                  <td>
                    <ExpandableSummary
                      items={group.summaryItems}
                      expandLongContent
                    />
                  </td>
                  <td>{group.created}</td>
                  <td>{group.updated}</td>
                  {canEditGroup && (
                    <td>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() =>
                          navigate(
                            `/user-management/access-groups/${group.id}/edit`,
                          )
                        }
                      >
                        Edit
                      </Button>
                      {/* <Button
                      className="delete-button"
                      variant="outlined"
                      size="small"
                      disabled
                      title="Delete endpoint is not included in the API collection"
                    >
                      Delete
                    </Button> */}
                    </td>
                  )}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <PaginationComponent
        count={pageCount}
        page={page}
        onChange={(_, value) => setPage(value)}
      />
      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={closeToast}
      >
        <Alert severity="success" variant="filled" onClose={closeToast}>
          {toast}
        </Alert>
      </Snackbar>
    </Layout>
  );
};

export const UserManagementAuditTrail = () => {
  const canExportAudit = hasPermission(
    PERMISSION_MODULES.USER_ACCESS_MANAGEMENT,
    USER_ACCESS_MANAGEMENT_PERMISSIONS.EXPORT_AUDIT_TRAIL,
  );
  const [records, setRecords] = useState([]);
  const [activities, setActivities] = useState([]);
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [activity, setActivity] = useState(
    USER_MANAGEMENT_TEXT.AUDIT_FILTER_ALL,
  );
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    UserManagementService.getAuditTrail({
      page,
      page_size: USER_MANAGEMENT_PAGE_SIZE,
      ...(activity !== USER_MANAGEMENT_TEXT.AUDIT_FILTER_ALL && { activity }),
    })
      .then((response) => {
        if (!mounted) return;
        setRecords(normalizeAuditTrail(response));
        setActivities(normalizeAuditActivityFilters(response));
        setPageCount(getPaginationCount(response, USER_MANAGEMENT_PAGE_SIZE));
      })
      .catch(
        (requestError) =>
          mounted &&
          setError(
            getApiErrorMessage(
              requestError,
              USER_MANAGEMENT_TEXT.LOAD_AUDIT_ERROR,
            ),
          ),
      )
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [activity, page]);

  const rows = records.filter((record) => {
    const searchableValue = [
      record.activity,
      record.name,
      record.updatedBy,
      record.ipAddress,
      ...record.previousPermissions,
      ...record.currentPermissions,
    ]
      .join(" ")
      .toLowerCase();
    const updatedOn = new Date(record.updatedOnValue);
    const hasValidDate = !Number.isNaN(updatedOn.getTime());
    const fromMatches =
      !fromDate ||
      (hasValidDate && updatedOn >= new Date(`${fromDate}T00:00:00`));
    const toMatches =
      !toDate || (hasValidDate && updatedOn <= new Date(`${toDate}T23:59:59`));
    return (
      searchableValue.includes(search.trim().toLowerCase()) &&
      fromMatches &&
      toMatches
    );
  });

  return (
    <Layout>
      <PageTitle>Audit Trail</PageTitle>
      <ManagementTabs>
        <Box className="audit-toolbar">
          <TextField
            size="small"
            placeholder="Search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <TextField
            size="small"
            type="date"
            label="From"
            value={fromDate}
            onChange={(event) => setFromDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: fromDate ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="Clear from date"
                    onClick={() => setFromDate("")}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
          <TextField
            size="small"
            type="date"
            label="To"
            value={toDate}
            onChange={(event) => setToDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: toDate ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="Clear to date"
                    onClick={() => setToDate("")}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
          <Select
            size="small"
            value={activity}
            onChange={(event) => {
              setActivity(event.target.value);
              setPage(1);
            }}
            aria-label="Filter audit activity"
          >
            <MenuItem value={USER_MANAGEMENT_TEXT.AUDIT_FILTER_ALL}>
              {USER_MANAGEMENT_TEXT.AUDIT_FILTER_ALL}
            </MenuItem>
            {activities.map((item) => (
              <MenuItem key={item} value={item}>
                {item}
              </MenuItem>
            ))}
          </Select>
          {canExportAudit && (
            <PrimaryButton
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={() => downloadAuditCsv(rows)}
              disabled={!rows.length || loading}
            >
              Download
            </PrimaryButton>
          )}
        </Box>
      </ManagementTabs>
      <Box className="um-table-wrap audit-table-wrap">
        {error && <Alert severity="error">{error}</Alert>}
        <table className="um-table um-audit-table">
          <thead>
            <tr>
              <th className="um-serial-column">Sr No.</th>
              <th>Activity</th>
              <th>Name</th>
              <th>Previous Permissions</th>
              <th>Current Permissions</th>
              <th>Updated By</th>
              <th>Updated On</th>
              <th>IP Address</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr className="um-loading-row">
                <td className="um-loading-cell" colSpan={8}>
                  <Loader size={36} />
                </td>
              </tr>
            )}
            {!loading && !error && rows.length === 0 && (
              <tr>
                <td colSpan={8}>
                  <Box py={1} textAlign="center">
                    <Typography color="text.secondary">
                      {USER_MANAGEMENT_TEXT.NO_AUDIT_LOGS}
                    </Typography>
                  </Box>
                </td>
              </tr>
            )}
            {!loading &&
              rows.map((record, index) => (
                <tr key={record.id}>
                  <td className="um-serial-column">
                    {getPaginatedSerialNumber(
                      page,
                      USER_MANAGEMENT_PAGE_SIZE,
                      index,
                    )}
                  </td>
                  <td>{record.activity}</td>
                  <td>{record.name}</td>
                  <td>
                    <PermissionSummary
                      permissions={record.previousPermissions}
                      groups={record.previousPermissionGroups}
                    />
                  </td>
                  <td>
                    <PermissionSummary
                      permissions={record.currentPermissions}
                      groups={record.currentPermissionGroups}
                    />
                  </td>
                  <td>{record.updatedBy}</td>
                  <td>{record.updatedOn}</td>
                  <td>{record.ipAddress}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </Box>
      <PaginationComponent
        count={pageCount}
        page={page}
        onChange={(_, value) => setPage(value)}
      />
    </Layout>
  );
};

export const AccessGroupForm = () => {
  const navigate = useNavigate();
  const { groupId } = useParams();
  const editing = Boolean(groupId);
  const canEditGroupDetails =
    !editing ||
    hasPermission(
      PERMISSION_MODULES.USER_ACCESS_MANAGEMENT,
      USER_ACCESS_MANAGEMENT_PERMISSIONS.EDIT_ACCESS_GROUP,
    );
  const canUpdateGroupStatus =
    !editing ||
    hasPermission(
      PERMISSION_MODULES.USER_ACCESS_MANAGEMENT,
      USER_ACCESS_MANAGEMENT_PERMISSIONS.UPDATE_ACCESS_GROUP_STATUS,
    );
  const canUpdateGroupPermissions =
    !editing ||
    hasPermission(
      PERMISSION_MODULES.USER_ACCESS_MANAGEMENT,
      USER_ACCESS_MANAGEMENT_PERMISSIONS.UPDATE_ACCESS_GROUP_PERMISSIONS,
    );
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [permissionGroups, setPermissionGroups] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    Promise.all([
      UserManagementService.getModulesAndFeatures(),
      editing
        ? UserManagementService.getGroups({ page_size: 1000 })
        : Promise.resolve(null),
    ])
      .then(([permissionsResponse, groupsResponse]) => {
        if (!mounted) return;
        setPermissionGroups(normalizePermissionGroups(permissionsResponse));
        if (editing) {
          const group = normalizeGroups(groupsResponse).find(
            (item) => String(item.id) === String(groupId),
          );
          setName(group?.name || "");
          setDescription(group?.description || "");
          setIsActive(group?.isActive ?? true);
          setPermissions(group?.featureIds || []);
        }
      })
      .catch((requestError) =>
        setMessage({
          severity: "error",
          text: getApiErrorMessage(
            requestError,
            USER_MANAGEMENT_TEXT.LOAD_PERMISSIONS_ERROR,
          ),
        }),
      )
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [editing, groupId]);

  const save = async () => {
    if (
      editing &&
      !canEditGroupDetails &&
      !canUpdateGroupStatus &&
      !canUpdateGroupPermissions
    )
      return;
    if (!name.trim()) {
      setError("Group name is required");
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      const payload = editing
        ? {}
        : {
            group_name: name.trim(),
            description: description.trim(),
            is_active: isActive,
            feature_ids: permissions,
          };
      if (editing) {
        if (canEditGroupDetails) {
          payload.group_name = name.trim();
          payload.description = description.trim();
        }
        if (canUpdateGroupStatus) payload.is_active = isActive;
        if (canUpdateGroupPermissions) payload.feature_ids = permissions;
        await UserManagementService.updateGroup(groupId, payload);
      } else {
        await UserManagementService.createGroup(payload);
      }
      navigate("/user-management/access-groups", {
        replace: true,
        state: {
          toast: editing
            ? USER_MANAGEMENT_TEXT.UPDATE_GROUP_SUCCESS
            : USER_MANAGEMENT_TEXT.CREATE_GROUP_SUCCESS,
        },
      });
    } catch (requestError) {
      setMessage({
        severity: "error",
        text: getApiErrorMessage(
          requestError,
          editing
            ? USER_MANAGEMENT_TEXT.UPDATE_GROUP_ERROR
            : USER_MANAGEMENT_TEXT.CREATE_GROUP_ERROR,
        ),
      });
    } finally {
      setSaving(false);
    }
  };
  return (
    <Layout>
      <PageTitle>
        {editing ? "Edit Access Group" : "Create Access Group"}
      </PageTitle>
      <Crumb
        current={editing ? "Edit Access Group" : "Create Access Group"}
        second="Access Groups"
        onHome={() => navigate("/user-management/access-groups")}
      />
      {message && <Alert severity={message.severity}>{message.text}</Alert>}
      <Box className="group-details-fields">
        <label className="group-name-field">
          Group Name
          <TextField
            size="small"
            placeholder="Enter Name"
            value={name}
            error={Boolean(error)}
            helperText={error}
            onChange={(event) => {
              setName(event.target.value);
              setError("");
            }}
            disabled={!canEditGroupDetails}
          />
        </label>
        <label className="group-name-field">
          Description
          <TextField
            size="small"
            placeholder="Enter Description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            disabled={!canEditGroupDetails}
          />
        </label>
        <Box className="group-status-field">
          <Typography component="span">Group Status</Typography>
          <Box display="flex" alignItems="center">
            <Switch
              checked={isActive}
              onChange={(event) => setIsActive(event.target.checked)}
              color="success"
              disabled={!canUpdateGroupStatus}
            />
            <Typography component="span" variant="body2">
              {isActive ? "Active" : "Inactive"}
            </Typography>
          </Box>
        </Box>
      </Box>
      <div className="access-form-card">
        <h2>Configure Role Permissions</h2>
        <p>
          Select the modules and granular actions this user group is authorized
          to perform.
        </p>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <PermissionSelector
            groups={permissionGroups}
            selected={permissions}
            onChange={setPermissions}
            showSelectAll
            disabled={!canUpdateGroupPermissions}
          />
        )}
        <div className="form-action-row">
          <small>Changes will affect all assigned users</small>
          <div>
            <Button
              variant="outlined"
              onClick={() => navigate("/user-management/access-groups")}
            >
              Cancel
            </Button>
            <PrimaryButton onClick={save} disabled={saving || loading}>
              {saving ? "Saving..." : "Save Changes"}
            </PrimaryButton>
          </div>
        </div>
      </div>
    </Layout>
  );
};
